import { useEffect } from 'react';
import { useWorkspaceStore, type Workspace } from '@/stores/workspace.store';
import { workspaceService } from '@/services/workspace.service';
import { withRetry } from '@/lib/retry';
import { pickDefaultWorkspace } from '@/lib/workspace-select';
import { useAuthStore } from '@/stores/auth.store';

/** Field yang memengaruhi tampilan/aturan — objek baru hanya dipasang kalau berubah. */
function sameWorkspace(a: Workspace, b: Workspace): boolean {
    return a.name === b.name && a.ownerId === b.ownerId
        && JSON.stringify(a.enabledPlugins ?? []) === JSON.stringify(b.enabledPlugins ?? []);
}

export function useWorkspace() {
    const workspaces = useWorkspaceStore(s => s.workspaces);
    const currentWorkspace = useWorkspaceStore(s => s.currentWorkspace);
    const setWorkspaces = useWorkspaceStore(s => s.setWorkspaces);
    const setCurrent = useWorkspaceStore(s => s.setCurrent);
    const getPersistedId = useWorkspaceStore(s => s.getPersistedId);
    const loadStatus = useWorkspaceStore(s => s.loadStatus);
    const setLoadStatus = useWorkspaceStore(s => s.setLoadStatus);

    const fetchWorkspaces = async () => {
        setLoadStatus('loading');
        try {
            // Server sibuk sesaat (503 db_busy / jaringan) → coba ulang otomatis.
            const res = await withRetry(() => workspaceService.list());
            const list = (res.data as Workspace[]) || [];
            setWorkspaces(list);
            // Baca state TERBARU (bukan closure render) — fetch bisa selesai belakangan.
            const current = useWorkspaceStore.getState().currentWorkspace;
            const fresh = current ? list.find((w) => w._id === current._id) : undefined;
            if (current && fresh) {
                // Pilihan di sesi ini tetap; segarkan objeknya (Absensi bisa baru
                // diaktifkan/dimatikan owner sejak terakhir dimuat).
                if (!sameWorkspace(fresh, current)) setCurrent(fresh);
            } else {
                // Belum ada pilihan / workspace-nya hilang → aturan di workspace-select.ts:
                // utamakan workspace yang Absensinya AKTIF, lalu yang BUKAN milik user
                // (kantor), bukan sekadar list[0] (tiket Silvi/Voiz 4 Agu 2026).
                const next = pickDefaultWorkspace(list, {
                    persistedId: getPersistedId(),
                    userId: useAuthStore.getState().user?._id,
                });
                if (next || current) setCurrent(next);
            }
            setLoadStatus('ready');
            return list;
        } catch {
            // Jangan telan diam-diam: halaman menampilkan pesan + tombol "Coba lagi".
            setLoadStatus('error');
            return [];
        }
    };

    return { workspaces, currentWorkspace, loadStatus, fetchWorkspaces, setCurrent };
}

export function useEnsureWorkspace() {
    const { workspaces, currentWorkspace, fetchWorkspaces } = useWorkspace();
    useEffect(() => {
        if (workspaces.length === 0) fetchWorkspaces();
    }, []);
    return { workspaces, currentWorkspace };
}
